import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../../shared/api/client';

const AuthContext = createContext(null);
const normalizeUser = (payload) => {
  const rawUser = payload?.user || payload;
  if (!rawUser) return null;
  return {
    ...rawUser,
    initials: rawUser.initials || (rawUser.name ? rawUser.name.split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase() : ''),
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// codex.md RBAC: allowlist sets returned by GET /permissions/me. Empty sets
// mean "no permission" (fail-closed), NOT "not loaded yet" — loading is
// tracked separately via permissionsLoading.
const EMPTY_PERMS = { pages: new Set(), graphs: new Set() };

// Key in localStorage for the admin's role-impersonation preview. Lives
// only on the admin's machine; the backend still enforces the caller's
// real role. SUPER_ADMIN uses this to preview what a CFO or PM would see
// without logging out. Cleared on logout.
const IMPERSONATE_KEY = "rc.impersonateRole";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(EMPTY_PERMS);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  // null = no impersonation, a UserRole string = pretend we're that role.
  // Only honoured when the real user is SUPER_ADMIN (checked below).
  const [impersonateRole, _setImpersonateRole] = useState(() => {
    try { return localStorage.getItem(IMPERSONATE_KEY) || null; } catch { return null; }
  });
  const [impersonatePerms, setImpersonatePerms] = useState(EMPTY_PERMS);

  // Pull the caller's permission view. Called after every successful
  // authentication path (session restore, OAuth callback, dev login).
  const refreshPermissions = useCallback(async () => {
    try {
      const result = await api.getMyPermissions();
      setPermissions({
        pages:  new Set(Array.isArray(result?.pages)  ? result.pages  : []),
        graphs: new Set(Array.isArray(result?.graphs) ? result.graphs : []),
      });
    } catch (_) {
      // Fail closed — unknown view beats accidentally leaking a restricted
      // page after a transient 5xx. AppShell handles the empty state.
      setPermissions(EMPTY_PERMS);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Restore session from localStorage on every page load
    const storedUser = localStorage.getItem('user');
    const accessToken = localStorage.getItem('accessToken');

    if (storedUser && accessToken) {
      // Optimistically restore to avoid flash of login page
      setUser(normalizeUser(JSON.parse(storedUser)));
      // Verify token is still valid + fetch permissions in parallel.
      Promise.all([
        api.getCurrentUser()
          .then(userData => {
            const normalizedUser = normalizeUser(userData);
            setUser(normalizedUser);
            localStorage.setItem('user', JSON.stringify(normalizedUser));
          })
          .catch(() => {
            api.clearTokens();
            setUser(null);
          }),
        refreshPermissions(),
      ]).finally(() => setLoading(false));
    } else {
      setPermissionsLoading(false);
      setLoading(false);
    }
  }, [refreshPermissions]);

  // Re-fetch permissions when the tab regains focus so a user whose grid was
  // edited server-side by SUPER_ADMIN sees the change without a full reload.
  useEffect(() => {
    function onFocus() {
      if (user) refreshPermissions();
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, refreshPermissions]);

  // codex.md v3: short-interval polling for immediate(ish) reflection of
  // admin matrix changes. 10s cadence × backend cache (5s) means a CEO's
  // browser picks up a revocation within ~15s worst case. Pauses when the
  // tab is hidden (visibilitychange listener below flips the interval).
  useEffect(() => {
    if (!user) return undefined;
    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (document.visibilityState === 'visible') refreshPermissions();
      }, 10_000);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    if (document.visibilityState === 'visible') start();
    const onVis = () => { document.visibilityState === 'visible' ? start() : stop(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, refreshPermissions]);

  /** Called by the /auth/callback page after backend OAuth redirect */
  const setUserFromCallback = (accessToken, userJson) => {
    try {
      const parsedUser = typeof userJson === 'string' ? JSON.parse(decodeURIComponent(userJson)) : userJson;
      const normalizedUser = normalizeUser(parsedUser);
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);
    } catch (e) {
      console.error('Failed to parse OAuth callback params', e);
    }
  };

  const completeOAuthLogin = useCallback(async () => {
    const session = await api.refreshSession();
    const userData = normalizeUser(await api.getCurrentUser());
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    await refreshPermissions();
    return { accessToken: session.accessToken, user: userData };
  }, [refreshPermissions]);

  /** Redirect to backend which redirects to Google */
  const loginWithGoogle = useCallback(() => {
    // In prod the missing-env check in services/api.js has already thrown,
    // so reaching here means either dev (fallback OK) or a misconfigured
    // prod build we still shouldn't localhost-redirect real users from.
    const apiBase = process.env.REACT_APP_API_URL;
    if (!apiBase) {
      if (process.env.NODE_ENV === 'production') {
        throw new Error('FATAL: REACT_APP_API_URL must be set at build time');
      }
      window.location.href = 'http://localhost:5000/api/auth/google/redirect';
      return;
    }
    window.location.href = `${apiBase}/auth/google/redirect`;
  }, []);

  /** Legacy frontend Google credential flow */
  const handleGoogleLogin = async (credentialResponse) => {
    try {
      setLoading(true);
      const data = await api.googleAuth(credentialResponse.credential);
      setUser(normalizeUser(data));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    } finally {
      setLoading(false);
    }
  };

  const devLogin = async () => {
    try {
      setLoading(true);
      const data = await api.devLogin();
      setUser(normalizeUser(data));
      await refreshPermissions();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || error.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch (_) {
      api.clearTokens();
    } finally {
      setUser(null);
      setPermissions(EMPTY_PERMS);
      setImpersonatePerms(EMPTY_PERMS);
      _setImpersonateRole(null);
      try { localStorage.removeItem(IMPERSONATE_KEY); } catch {}
      setPermissionsLoading(false);
    }
  }, []);

  // codex.md v3: no hardcoded role bypass. SUPER_ADMIN and CEO both read
  // the same allowlist the backend enforces — if SUPER_ADMIN toggles a
  // CEO permission off in the matrix, the CEO must lose access. Only
  // exception: while we're still fetching the initial permissions
  // payload, allow the session to render its skeleton rather than showing
  // Access Denied everywhere. Once permissionsLoading is false, access is
  // 100% allowlist-driven.
  const realRole = user?.role;
  const isSuperAdminUser = realRole === 'SUPER_ADMIN';
  const impersonating = isSuperAdminUser && impersonateRole && impersonateRole !== realRole;
  const activeRole = impersonating ? impersonateRole : realRole;
  const activePerms = impersonating ? impersonatePerms : permissions;
  const canSeePage  = useCallback((pageKey)  => activePerms.pages.has(pageKey),  [activePerms]);
  const canSeeGraph = useCallback((graphKey) => activePerms.graphs.has(graphKey), [activePerms]);

  // SUPER_ADMIN-only: set/clear the impersonation target. When set, canSeePage
  // and canSeeGraph treat the caller as if they had that role, so the admin
  // can verify a matrix toggle without logging out. The pretend role's grid
  // is pulled via the admin GET /api/permissions; we filter locally to keep
  // network traffic low.
  const setImpersonateRole = useCallback(async (role) => {
    if (!isSuperAdminUser) return;
    if (!role || role === realRole) {
      _setImpersonateRole(null);
      setImpersonatePerms(EMPTY_PERMS);
      try { localStorage.removeItem(IMPERSONATE_KEY); } catch {}
      return;
    }
    _setImpersonateRole(role);
    try { localStorage.setItem(IMPERSONATE_KEY, role); } catch {}
    try {
      const grid = await api.getPermissionGrid();
      const pages  = new Set((grid?.pages  || []).filter(r => r.role === role && r.isVisible).map(r => r.pageKey));
      const graphs = new Set((grid?.graphs || []).filter(r => r.role === role && r.isVisible).map(r => r.graphKey));
      setImpersonatePerms({ pages, graphs });
    } catch {
      setImpersonatePerms(EMPTY_PERMS);
    }
  }, [isSuperAdminUser, realRole]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      loginWithGoogle,
      setUserFromCallback,
      completeOAuthLogin,
      login: handleGoogleLogin,
      devLogin,
      logout,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN',
      isSuperAdmin: user?.role === 'SUPER_ADMIN',
      // codex.md RBAC — page + graph allowlist helpers. Backend is the
      // source of truth; these just save a round-trip on every render.
      permissions: activePerms,
      permissionsLoading,
      canSeePage,
      canSeeGraph,
      refreshPermissions,
      // SUPER_ADMIN preview — pretend to be another role without relogging.
      // activeRole === realRole means not impersonating. setImpersonateRole(null) clears.
      realRole,
      activeRole,
      impersonating,
      setImpersonateRole,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
